import React, {useState, useEffect} from "react";
import { useNavigate } from "react-router-dom";
import { isWhiteLabel } from "../services/agencyService";
import DisclosureText from "./DisclosureText";
import campaignService from "../services/campaignService";
import { NewsletterEditionLink } from "../types/newsletterEdition";
import { ReactComponent as TrashIcon } from "../assets/icons/trash.svg";
import SidePanel from "./SidePanel";

const Sources: React.FC = () => {
  const navigate = useNavigate();
  const [sources, setSources] = useState<NewsletterEditionLink[]>([]);
  const [updatedSourcesSuccess, setUpdatedSourcesSuccess] = useState(false);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const addNewSource = () => {
    setSources([...sources, { url: "", instructions: "", original_content: false }]);
  };

  const removeSource = (index: number) => {
    const newSources = sources.filter((_, i) => i !== index);
    setSources(newSources);
  };

  const updateSource = (index: number, field: keyof NewsletterEditionLink, value: string | boolean) => {
    const newSources = [...sources];
    (newSources[index] as any)[field] = value;
    setSources(newSources);
  };

  const updateNewsletterSources = async () => {
    const validSources = sources
      .filter(source => source.url && source.url.trim() !== "")
      .map(source => ({
        url: source.url,
        instructions: source.instructions || "",
        original_content: source.original_content || false
      }));

    try {
      setUpdatedSourcesSuccess(false);
      const updatedSources = await campaignService.updateNewsletterSources(validSources);
      setUpdatedSourcesSuccess(true);

      // clear success banner
      setTimeout(() => {
        setUpdatedSourcesSuccess(false);
      }, 1500);

      setSources(updatedSources);
    } catch (e) {
      console.error("Failed to update sources", e);
    }
  };

  const fetchSources = async () => {
    return campaignService.getNewsletterSources();
  };

  useEffect(() => {
    fetchSources().then(fetchedSources => {
      setSources(fetchedSources.length > 0 ? fetchedSources : [{ url: "", instructions: "", original_content: false }]);
    });
  }, []);

  return (
    <div style={{  maxWidth: "1200px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1>Sources</h1>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button
            onClick={addNewSource}
            className="tertiary-info-button"
            style={{ height: "45px", marginTop: "0" }}
          >
            Add a Source
          </button>
          <button
            onClick={updateNewsletterSources}
            className="primary-button"
            style={{ marginTop: "0" }}
          >
            Save Sources
          </button>
        </div>
      </div>

      <p style={{ marginBottom: "20px" }}>
        This is the list of preferred websites {isWhiteLabel() ? "our AI or team" : "Potions AI or team"} will search for content to repurpose from on a regular basis. {" "}
        <button
          onClick={() => setIsSidePanelOpen(true)}
          style={{
            color: "#6366f1",
            textDecoration: "none",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "0",
            fontSize: "inherit"
          }}
        >
          Learn More.
        </button>
      </p>

      {sources.length > 0 && (
        <div style={{ 
          border: "1px solid #e5e7eb", 
          borderRadius: "8px", 
          overflow: "hidden",
          backgroundColor: "white"
        }}>
          {/* Header */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 140px 60px",
            gap: "32px",
            padding: "12px 16px",
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
            fontSize: "12px",
            fontWeight: "600",
            color: "#6b7280",
            textTransform: "uppercase",
            letterSpacing: "0.05em"
          }}>
            <div>Website</div>
            <div>Additional Instructions</div>
            <div style={{ textAlign: "center" }}>Original Content</div>
            <div></div>
          </div>

          {/* Rows */}
          {sources.map((source, index) => (
            <div
              key={index}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 140px 60px",
                gap: "48px",
                padding: "12px 16px",
                borderBottom: index < sources.length - 1 ? "1px solid #e5e7eb" : "none",
                alignItems: "center"
              }}
            >
              <input
                type="text"
                value={source.url}
                onChange={(e) => updateSource(index, "url", e.target.value)}
                placeholder="https://www.linkedin.com/in/evan-kozliner/"
                style={{
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                  outline: "none",
                  width: "100%"
                }}
              />
              
              <textarea
                value={source.instructions || ""}
                onChange={(e) => updateSource(index, "instructions", e.target.value)}
                placeholder="This is my LinkedIn. Repurpose posts about email marketing..."
                style={{
                  padding: "8px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  fontSize: "14px",
                  outline: "none",
                  width: "100%",
                  resize: "vertical",
                  height: "15px",
                  fontFamily: "inherit",
                  overflow: "hidden"
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  // Reset height to measure scrollHeight
                  target.style.height = "40px";
                  // Only expand if content exceeds single line
                  if (target.scrollHeight > 40) {
                    target.style.height = target.scrollHeight + "px";
                    target.style.overflow = "auto";
                  } else {
                    target.style.overflow = "hidden";
                  }
                }}
              />

              <div style={{ display: "flex", justifyContent: "center" }}>
                <input
                  type="checkbox"
                  checked={source.original_content || false}
                  onChange={(e) => updateSource(index, "original_content", e.target.checked)}
                  style={{
                    width: "16px",
                    height: "16px",
                    cursor: "pointer"
                  }}
                />
              </div>

              <TrashIcon
                onClick={() => removeSource(index)}
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                  color: "#6b7280"
                }}
              />
            </div>
          ))}
        </div>
      )}

      {updatedSourcesSuccess && (
        <div
          style={{
            backgroundColor: "#e0f7df",
            color: "#2e7d32",
            border: "1px solid #2e7d32",
            borderRadius: "8px",
            padding: "8px 16px",
            marginTop: "16px",
            marginBottom: "16px",
          }}>
          ✅
          <span style={{marginLeft: "12px"}}>Sources successfully updated!</span>
        </div>
      )}

      <SidePanel isOpen={isSidePanelOpen} onClose={() => setIsSidePanelOpen(false)}>
        <div>
          <h1 style={{ marginBottom: "24px", marginTop: "0" }}>
            Examples and Details.
          </h1>
          
          <h3 style={{ fontWeight: "bold", marginBottom: "16px", marginTop: "24px" }}>
            Examples
          </h3>
          
          <ul style={{ paddingLeft: "20px", marginBottom: "24px", lineHeight: "1.6" }}>
            <li style={{ marginBottom: "12px" }}>
              <strong>Add your LinkedIn page</strong> and mark it as original content, and we will repurpose your recent posts for your newsletter. Because you've marked it original content, our AI can write knowing that you are the author. You can add additional instructions to filter particular posts from our search.
            </li>
            <li style={{ marginBottom: "12px" }}>
              <strong>Add in your favorite industry news website</strong> (e.g.{" "}
              <code style={{ 
                backgroundColor: "#f3f4f6", 
                padding: "2px 4px", 
                borderRadius: "4px", 
                fontSize: "13px" 
              }}>
                techcrunch.com
              </code>
              ) and tell us in instructions that you only want AI related news. We'll read the latest AI articles and create newsletter content with them.
            </li>
          </ul>
          
          <h3 style={{ fontWeight: "bold", marginBottom: "16px" }}>
            Details
          </h3>
          
          <ul style={{ paddingLeft: "20px", marginBottom: "24px", lineHeight: "1.6" }}>
            <li style={{ marginBottom: "12px" }}>
              {isWhiteLabel() ? "We" : "Potions"} will{" "}
              <strong style={{ color: "#059669" }}>focus</strong>{" "}
              on the sources you provide, but we may also draw from other high-quality sources we find relevant to your needs.
            </li>
            <li style={{ marginBottom: "12px" }}>
              {isWhiteLabel() ? "We" : "Potions"}{" "}
              <strong style={{ color: "#dc2626" }}>never</strong>{" "}
              plagiarizes content or bypasses paywalls. At most, we'll give a short summary of the content you provide, and link back to the original source.
            </li>
            <li style={{ marginBottom: "12px" }}>
              {isWhiteLabel() ? "We" : "Potions"} may change your sources based on your feedback or what we see working.
            </li>
          </ul>
          
          <div style={{ 
            backgroundColor: "#f8fafc", 
            border: "1px solid #e2e8f0", 
            borderRadius: "8px", 
            padding: "16px",
            marginTop: "24px"
          }}>
            <h4 style={{ 
              fontWeight: "bold", 
              marginBottom: "8px", 
              marginTop: "0",
              color: "#374151" 
            }}>
              💡 Pro Tip
            </h4>
            <p style={{ 
              marginBottom: "0", 
              color: "#6b7280",
              lineHeight: "1.5"
            }}>
              The more specific your additional instructions, the better we can tailor the content to your needs. For example: "Focus on early-stage startup funding news" or "Only include posts about email marketing strategies."
            </p>
          </div>
        </div>
      </SidePanel>
    </div>
  );
};

export default Sources;
